import { useEffect, useState } from 'react';

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-16 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/1c7114c9-6135-4184-9e24-b9281d8cdcd3.png" alt="KATEKO OOD Logo" className="h-12 w-auto" />
            <h1 className="text-2xl font-bold text-[#2C2C2C]">KATEKO OOD</h1>
          </div>
          <div className="flex items-center gap-10">
            <button onClick={() => scrollToSection('home')} className="text-base font-medium text-[#4A4A4A] hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-base font-medium text-[#4A4A4A] hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-base font-medium text-[#4A4A4A] hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-base font-medium text-[#4A4A4A] hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-base font-medium text-[#4A4A4A] hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-24 bg-gradient-to-b from-[#F0F7FF] to-white min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        <div className="max-w-[1200px] mx-auto px-16 w-full relative z-10">
          <div className="flex items-center gap-16">
            <div className="w-[55%]">
              <h2 className="text-[56px] font-extrabold text-[#2C2C2C] leading-tight mb-8">Expert iOS Development Team</h2>
              <p className="text-[22px] font-light text-[#6B6B6B] leading-relaxed">Creating modern VPN and Cleaner applications that enhance security and user experience on mobile devices</p>
            </div>
            <div className="w-[45%] flex justify-center">
              <img src="https://public.readdy.ai/ai/img_res/e198e4af98fad75c8f3df682a543e978.jpg" alt="iOS Development" className="w-full h-auto object-cover rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-16">
          <div className="flex gap-16">
            <div className="w-[40%]">
              <p className="text-xs font-semibold text-[#0066FF] uppercase tracking-[2px] mb-4">ABOUT US</p>
              <h3 className="text-[42px] font-bold text-[#2C2C2C] leading-tight">Building the Future of iOS Applications</h3>
            </div>
            <div className="w-[60%]">
              <p className="text-lg text-[#555555] leading-relaxed mb-6">
                KATEKO OOD is a dedicated team of experts specializing in developing cutting-edge applications for the iOS platform. Our mission is to create innovative solutions that not only meet but exceed the expectations of modern mobile users. With years of experience in the industry, we understand the unique challenges and opportunities that come with iOS development.
              </p>
              <p className="text-lg text-[#555555] leading-relaxed">
                Our flagship products, including our advanced VPN and comprehensive Cleaner applications, are meticulously designed to enhance user experience while prioritizing security and privacy. We believe that every user deserves access to tools that protect their digital life and optimize their device performance. Our commitment to excellence drives us to continuously innovate and improve our offerings, ensuring that our applications remain at the forefront of mobile technology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-28 bg-[#F8F9FA]">
        <div className="max-w-[1200px] mx-auto px-16">
          <div className="text-center mb-20">
            <p className="text-xs font-semibold text-[#0066FF] uppercase tracking-[2px] mb-5">OUR PRODUCTS</p>
            <h3 className="text-[48px] font-bold text-[#2C2C2C]">iOS Solutions for Modern Users</h3>
          </div>
          <div className="grid grid-cols-2 gap-12">
            {/* VPN Product */}
            <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-shadow duration-300 p-12">
              <div className="w-20 h-20 bg-[#E8F4FF] rounded-full flex items-center justify-center mb-8">
                <i className="ri-shield-keyhole-line text-[40px] text-[#0066FF]"></i>
              </div>
              <h4 className="text-[32px] font-bold text-[#2C2C2C] mb-4">VPN Application</h4>
              <p className="text-base text-[#666666] leading-relaxed">
                Our VPN application provides enterprise-grade security for iOS users who value their privacy and digital freedom. With military-grade encryption protocols, your online activities remain completely private and secure from prying eyes. The application features lightning-fast connection speeds across our global network of servers, ensuring you never have to compromise between security and performance. Our intuitive interface makes it incredibly easy to connect to any server location with just a single tap. Advanced features include automatic kill switch protection, DNS leak prevention, and split tunneling capabilities. Whether you're browsing on public Wi-Fi, accessing geo-restricted content, or simply want to maintain your online anonymity, our VPN application delivers uncompromising protection. The app is optimized for iOS, ensuring minimal battery drain while maintaining maximum security. Regular updates keep you protected against the latest threats, and our dedicated support team is always ready to assist you.
              </p>
            </div>

            {/* Cleaner Product */}
            <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-shadow duration-300 p-12">
              <div className="w-20 h-20 bg-[#E8F4FF] rounded-full flex items-center justify-center mb-8">
                <i className="ri-brush-line text-[40px] text-[#0066FF]"></i>
              </div>
              <h4 className="text-[32px] font-bold text-[#2C2C2C] mb-4">Cleaner Application</h4>
              <p className="text-base text-[#666666] leading-relaxed">
                Transform your iOS device performance with our comprehensive Cleaner application, designed to optimize storage and boost speed effortlessly. This powerful tool intelligently identifies and removes unnecessary files, duplicate photos, large video files, and cached data that accumulate over time and slow down your device. Our advanced scanning algorithms work quickly to analyze your entire device, presenting you with clear recommendations on what can be safely removed. The application features a beautiful, user-friendly interface that makes device maintenance simple and stress-free. With smart photo management, you can easily identify similar or blurry photos and free up valuable storage space. The app also includes a contact cleaner to remove duplicate entries, a calendar cleaner for old events, and a comprehensive media manager. Real-time storage monitoring keeps you informed about your device's health, while scheduled cleaning ensures your iPhone or iPad always runs at peak performance. Experience the difference of a cleaner, faster iOS device.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-28 bg-white">
        <div className="max-w-[1100px] mx-auto px-16">
          <div className="text-center mb-20">
            <p className="text-xs font-semibold text-[#0066FF] uppercase tracking-[2px] mb-5">KEY FEATURES</p>
            <h3 className="text-[44px] font-bold text-[#2C2C2C]">What Makes Our Apps Stand Out</h3>
          </div>
          <div className="grid grid-cols-3 gap-10">
            <div className="bg-[#FAFBFC] rounded-2xl p-10 hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-[48px] text-[#0066FF]"></i>
              </div>
              <h4 className="text-2xl font-semibold text-[#2C2C2C] mb-4">Advanced Security</h4>
              <p className="text-[15px] text-[#666666] leading-relaxed">
                Our applications implement the highest security standards in the industry, utilizing end-to-end encryption and secure protocols to protect your data. Every feature is built with security-first principles, ensuring your information remains private and protected at all times.
              </p>
            </div>

            <div className="bg-[#FAFBFC] rounded-2xl p-10 hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-rocket-line text-[48px] text-[#0066FF]"></i>
              </div>
              <h4 className="text-2xl font-semibold text-[#2C2C2C] mb-4">High Performance</h4>
              <p className="text-[15px] text-[#666666] leading-relaxed">
                Optimized specifically for iOS devices, our apps deliver exceptional performance without draining your battery. We use efficient algorithms and native iOS frameworks to ensure smooth operation, fast response times, and minimal resource consumption for the best user experience.
              </p>
            </div>

            <div className="bg-[#FAFBFC] rounded-2xl p-10 hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-palette-line text-[48px] text-[#0066FF]"></i>
              </div>
              <h4 className="text-2xl font-semibold text-[#2C2C2C] mb-4">Intuitive Interface</h4>
              <p className="text-[15px] text-[#666666] leading-relaxed">
                Our user interface follows Apple's Human Interface Guidelines, providing a familiar and intuitive experience. Every element is carefully designed for ease of use, with clear navigation, beautiful animations, and thoughtful interactions that make complex tasks simple and enjoyable.
              </p>
            </div>

            <div className="bg-[#FAFBFC] rounded-2xl p-10 hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-eye-off-line text-[48px] text-[#0066FF]"></i>
              </div>
              <h4 className="text-2xl font-semibold text-[#2C2C2C] mb-4">Privacy Protection</h4>
              <p className="text-[15px] text-[#666666] leading-relaxed">
                We take your privacy seriously and never collect, store, or share your personal data. Our applications operate with complete transparency, giving you full control over your information. No tracking, no ads, no compromises on your digital privacy.
              </p>
            </div>

            <div className="bg-[#FAFBFC] rounded-2xl p-10 hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-settings-3-line text-[48px] text-[#0066FF]"></i>
              </div>
              <h4 className="text-2xl font-semibold text-[#2C2C2C] mb-4">Easy Management</h4>
              <p className="text-[15px] text-[#666666] leading-relaxed">
                Manage all aspects of our applications with simple, straightforward controls. Customize settings to match your preferences, schedule automated tasks, and monitor performance with comprehensive dashboards. Everything you need is just a tap away, organized logically and accessibly.
              </p>
            </div>

            <div className="bg-[#FAFBFC] rounded-2xl p-10 hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-refresh-line text-[48px] text-[#0066FF]"></i>
              </div>
              <h4 className="text-2xl font-semibold text-[#2C2C2C] mb-4">Regular Updates</h4>
              <p className="text-[15px] text-[#666666] leading-relaxed">
                Stay ahead with frequent updates that bring new features, performance improvements, and security enhancements. Our development team continuously works to refine and expand our applications, ensuring compatibility with the latest iOS versions and addressing user feedback promptly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <footer id="contact" className="bg-[#1A1A1A] pt-20 pb-10">
        <div className="max-w-[1200px] mx-auto px-16">
          <div className="flex items-start gap-4 mb-12">
            <img src="https://public.readdy.ai/ai/img_res/1c7114c9-6135-4184-9e24-b9281d8cdcd3.png" alt="KATEKO OOD Logo" className="h-12 w-auto" />
            <h3 className="text-[48px] font-bold text-white">KATEKO OOD</h3>
          </div>
          
          <div className="grid grid-cols-3 gap-16 mb-16">
            <div>
              <p className="text-base text-white/80 leading-relaxed mb-10">
                We are a team of passionate iOS developers dedicated to creating applications that make a real difference in people's digital lives. Our commitment to quality, security, and user experience drives everything we do. Join thousands of satisfied users who trust KATEKO OOD for their mobile security and device optimization needs.
              </p>
              <div className="flex gap-5">
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-xl text-white hover:text-[#1A1A1A]"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-all cursor-pointer">
                  <i className="ri-twitter-fill text-xl text-white hover:text-[#1A1A1A]"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-xl text-white hover:text-[#1A1A1A]"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-all cursor-pointer">
                  <i className="ri-instagram-line text-xl text-white hover:text-[#1A1A1A]"></i>
                </a>
              </div>
            </div>

            <div className="space-y-9">
              <div>
                <p className="text-[11px] font-semibold text-white uppercase tracking-[1.5px] mb-3">ADDRESS</p>
                <p className="text-base text-white">149 Brezovsko shose str.</p>
                <p className="text-base text-white">City Plovdiv, Zip 4003</p>
                <p className="text-base text-white">Country Bulgaria</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-white uppercase tracking-[1.5px] mb-3">PHONE</p>
                <p className="text-base text-white">+1 2178820039</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-white uppercase tracking-[1.5px] mb-3">EMAIL</p>
                <p className="text-base text-white">help@katekoood.com</p>
              </div>
            </div>

            <div>
              <img src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20iOS%20development%20team%20working%20together%20in%20bright%20contemporary%20office%20space%2C%20developers%20at%20workstations%20with%20multiple%20monitors%2C%20clean%20professional%20environment%2C%20natural%20daylight%2C%20teamwork%20atmosphere%2C%20technology%20workspace%20aesthetic&width=400&height=550&seq=contact001&orientation=portrait" alt="Team Collaboration" className="w-full h-auto object-cover rounded-2xl shadow-xl" />
            </div>
          </div>

          <div className="border-t border-[#333333] pt-8">
            <div className="flex justify-between items-center">
              <p className="text-sm text-white/70">© 2025 KATEKO OOD. All rights reserved.</p>
              <div className="flex gap-8">
                <a href="#" className="text-sm text-white/70 hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</a>
                <a href="#" className="text-sm text-white/70 hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">Terms of Service</a>
                <a href="#" className="text-sm text-white/70 hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">Cookies</a>
              </div>
            </div>
            <div className="mt-6 text-center">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-[#0066FF] transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
